"use client";

import {createFormControl} from "@gluestack-ui/form-control";
import type {VariantProps} from "@gluestack-ui/nativewind-utils";
import {withStyleContext} from "@gluestack-ui/nativewind-utils/withStyleContext";
import React from "react";

import {Box} from "../box";
import {PrimitiveIcon} from "../primitive-icon";
import {Text} from "../text";

import {
    formControlErrorIconStyle,
    formControlErrorStyle,
    formControlErrorTextStyle,
    formControlHelperStyle,
    formControlHelperTextStyle,
    formControlLabelAstrickStyle,
    formControlLabelStyle,
    formControlLabelTextStyle,
    formControlStyle
} from "./styles";

const SCOPE = "FORM_CONTROL";

type IFormControlLabelAstrickProps = React.ComponentPropsWithoutRef<typeof Text> &
    VariantProps<typeof formControlLabelAstrickStyle>;

const FormControlLabelAstrick = React.forwardRef<
    React.ElementRef<typeof Text>,
    IFormControlLabelAstrickProps
>(({className, ...props}, ref) => {
    return (
        <Text
            ref={ref}
            className={formControlLabelAstrickStyle({
                class: className
            })}
            {...props}
        />
    );
});

export const UIFormControl = createFormControl({
    Root: withStyleContext(Box, SCOPE),
    Error: Box,
    ErrorText: Text,
    ErrorIcon: PrimitiveIcon,
    Label: Box,
    LabelText: Text,
    LabelAstrick: FormControlLabelAstrick,
    Helper: Box,
    HelperText: Text
});

type IFormControlProps = React.ComponentProps<typeof UIFormControl> &
    VariantProps<typeof formControlStyle>;

const FormControl = React.forwardRef<React.ElementRef<typeof UIFormControl>, IFormControlProps>(
    ({className, size = "md", ...props}, ref) => {
        return (
            <UIFormControl
                ref={ref}
                className={formControlStyle({size, class: className})}
                {...props}
                context={{size}}
            />
        );
    }
);

type IFormControlErrorProps = React.ComponentProps<typeof UIFormControl.Error> &
    VariantProps<typeof formControlErrorStyle>;

const FormControlError = React.forwardRef<
    React.ElementRef<typeof UIFormControl.Error>,
    IFormControlErrorProps
>(({className, ...props}, ref) => {
    return (
        <UIFormControl.Error
            ref={ref}
            className={formControlErrorStyle({class: className})}
            {...props}
        />
    );
});

type IFormControlErrorTextProps = React.ComponentProps<typeof UIFormControl.Error.Text> &
    VariantProps<typeof formControlErrorTextStyle>;

const FormControlErrorText = React.forwardRef<
    React.ElementRef<typeof UIFormControl.Error.Text>,
    IFormControlErrorTextProps
>(({className, ...props}, ref) => {
    return (
        <UIFormControl.Error.Text
            ref={ref}
            className={formControlErrorTextStyle({
                class: className
            })}
            {...props}
        />
    );
});

type IFormControlErrorIconProps = React.ComponentProps<typeof UIFormControl.Error.Icon> &
    VariantProps<typeof formControlErrorIconStyle> & {
        height?: number;
        width?: number;
    };

const FormControlErrorIcon = React.forwardRef<
    React.ElementRef<typeof UIFormControl.Error.Icon>,
    IFormControlErrorIconProps
>(({className, size, ...props}, ref) => {
    if (typeof size === "number") {
        return (
            <UIFormControl.Error.Icon
                ref={ref}
                {...props}
                className={formControlErrorIconStyle({class: className})}
                size={size}
            />
        );
    }
    if ((props.height !== undefined || props.width !== undefined) && size === undefined) {
        return (
            <UIFormControl.Error.Icon
                ref={ref}
                {...props}
                className={formControlErrorIconStyle({class: className})}
            />
        );
    }
    return (
        <UIFormControl.Error.Icon
            className={formControlErrorIconStyle({
                class: className
            })}
            {...props}
        />
    );
});

type IFormControlLabelProps = React.ComponentProps<typeof UIFormControl.Label> &
    VariantProps<typeof formControlLabelStyle>;

const FormControlLabel = React.forwardRef<
    React.ElementRef<typeof UIFormControl.Label>,
    IFormControlLabelProps
>(({className, ...props}, ref) => {
    return (
        <UIFormControl.Label
            ref={ref}
            className={formControlLabelStyle({class: className})}
            {...props}
        />
    );
});

type IFormControlLabelTextProps = React.ComponentProps<typeof UIFormControl.Label.Text> &
    VariantProps<typeof formControlLabelTextStyle>;

const FormControlLabelText = React.forwardRef<
    React.ElementRef<typeof UIFormControl.Label.Text>,
    IFormControlLabelTextProps
>(({className, ...props}, ref) => {
    return (
        <UIFormControl.Label.Text
            ref={ref}
            className={formControlLabelTextStyle({
                class: className
            })}
            {...props}
        />
    );
});

type IFormControlHelperProps = React.ComponentProps<typeof UIFormControl.Helper> &
    VariantProps<typeof formControlHelperStyle>;

const FormControlHelper = React.forwardRef<
    React.ElementRef<typeof UIFormControl.Helper>,
    IFormControlHelperProps
>(({className, ...props}, ref) => {
    return (
        <UIFormControl.Helper
            ref={ref}
            className={formControlHelperStyle({
                class: className
            })}
            {...props}
        />
    );
});

type IFormControlHelperTextProps = React.ComponentProps<typeof UIFormControl.Helper.Text> &
    VariantProps<typeof formControlHelperTextStyle>;

const FormControlHelperText = React.forwardRef<
    React.ElementRef<typeof UIFormControl.Helper.Text>,
    IFormControlHelperTextProps
>(({className, ...props}, ref) => {
    return (
        <UIFormControl.Helper.Text
            ref={ref}
            className={formControlHelperTextStyle({
                class: className
            })}
            {...props}
        />
    );
});

FormControl.displayName = "FormControl";
FormControlError.displayName = "FormControlError";
FormControlErrorText.displayName = "FormControlErrorText";
FormControlErrorIcon.displayName = "FormControlErrorIcon";
FormControlLabel.displayName = "FormControlLabel";
FormControlLabelText.displayName = "FormControlLabelText";
FormControlLabelAstrick.displayName = "FormControlLabelAstrick";
FormControlHelper.displayName = "FormControlHelper";
FormControlHelperText.displayName = "FormControlHelperText";

export {
    FormControl,
    FormControlError,
    FormControlErrorText,
    FormControlErrorIcon,
    FormControlLabel,
    FormControlLabelText,
    FormControlLabelAstrick,
    FormControlHelper,
    FormControlHelperText
};
